/**
 * sessionReconcile — 세션 제어 패널의 순수 파생 로직 (#3).
 *
 * 세션 상태머신/전이는 `@muzly/shared`의 `usePitchSession`(순수 머신 + 네이티브
 * 배선)이 소유한다. 여기 남은 것은 라이브 인디케이터용 파생값(경과시간·마지막
 * 프레임 경과·stale) 계산뿐이며, 모두 부수효과 없는 순수 함수다 — 벽시계(now)는
 * 인자로 주입한다(vitest 가능).
 */

export type SessionState = 'idle' | 'running' | 'paused';
export type SessionCommand = 'start' | 'stop' | 'pause' | 'resume';

/** 이 시간 이상 무프레임이면 running이라도 "무입력/멈춤"으로 간주해 표시한다. */
export const STALE_AFTER_MS = 2000;

/** deriveLiveness가 읽는 최소 상태 — 훅에서 추적한 타이밍을 주입한다. */
export interface LivenessInput {
  session: SessionState;
  /** running 시작 벽시계(ms) — 경과시간 계산용. */
  startedAtMs: number | null;
  /** 마지막 pitch_frame 도착 벽시계(ms) — "마지막 프레임 N초 전" 계산용. */
  lastFrameAtMs: number | null;
}

export interface Liveness {
  /** running/paused 경과시간(ms). idle이거나 시작시각 미상이면 null. */
  elapsedMs: number | null;
  /** 마지막 프레임 이후 경과(ms). 프레임이 아직 없으면 null. */
  sinceLastFrameMs: number | null;
  /** running인데 STALE_AFTER_MS 이상 무프레임 — "멈춤/무입력"으로 표시. */
  stalled: boolean;
  /** running + 최근 프레임 수신 중 — 맥동 점을 켤지. */
  live: boolean;
}

/** 벽시계(nowMs) 기준 라이브 인디케이터 파생값. 순수 — now 주입. */
export const deriveLiveness = (
  state: LivenessInput,
  nowMs: number,
  staleAfterMs: number = STALE_AFTER_MS,
): Liveness => {
  const active = state.session === 'running' || state.session === 'paused';
  const elapsedMs =
    active && state.startedAtMs !== null ? nowMs - state.startedAtMs : null;
  const sinceLastFrameMs =
    state.lastFrameAtMs !== null ? nowMs - state.lastFrameAtMs : null;

  if (state.session !== 'running') {
    return { elapsedMs, sinceLastFrameMs, stalled: false, live: false };
  }
  // 프레임 도착 후엔 그 간격으로, 시작 직후 무프레임이면 경과시간으로 판정.
  const gapMs = sinceLastFrameMs ?? elapsedMs ?? 0;
  const stalled = gapMs >= staleAfterMs;
  return {
    elapsedMs,
    sinceLastFrameMs,
    stalled,
    live: !stalled && state.lastFrameAtMs !== null,
  };
};

/** ms → mm:ss. 음수/null은 00:00. */
export const formatElapsed = (ms: number | null): string => {
  const total = ms === null || ms < 0 || !Number.isFinite(ms) ? 0 : ms;
  const secs = Math.floor(total / 1000);
  const mm = Math.floor(secs / 60);
  const ss = secs % 60;
  return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
};

/** "N초 전" 라벨. 프레임 없으면 '—'. */
export const formatSinceFrame = (ms: number | null): string => {
  if (ms === null || ms < 0 || !Number.isFinite(ms)) {
    return '—';
  }
  return `${Math.floor(ms / 1000)}초 전`;
};
